import React from 'react';

import { motion, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';

interface QuickActionButtonProps {
  icon: React.ReactNode;
  text: string;
  subtext?: string;
  onClick: () => void;
  to?: string;
  className?: string;
}

interface QuickActionsModalProps {
  actions: QuickAction[];
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface QuickAction {
  icon: React.ReactNode;
  text: string;
  subtext?: string;
  onClick: () => void;
  to?: string;
  className?: string;
}

const overlayVariants: Variants = {
  hidden: { opacity: 0, display: "none" },
  visible: { opacity: 0.6, display: "block" }
};

const modalVariants: Variants = {
  hidden: {
    display: "none",
    opacity: 0,
    y: 50,
    scale: 0.95,
  },
  visible: {
    display: "block",
    opacity: 1,
    y: 0,
    scale: 1,
  }
};

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ icon, text, subtext, onClick, to, className }) => {
  const content = (
    <div className='flex gap-4 items-center'>
      <span className='shrink-0'>{icon}</span>
      <div className='flex flex-col text-left'>
        <p className="font-semibold text-base text-white">{text}</p>
        <p className="text-sm text-white">{subtext}</p>
      </div>
    </div>
  );

  return (
    <motion.button
      className={`flex p-4 bg-[#1D2233] ${className}`}
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
    >
      {to ? (
        <Link className='w-full' to={to}>
          {content}
        </Link>
      ) : content}
    </motion.button>
  );
};

export const QuickActionsModal: React.FC<QuickActionsModalProps> = ({ actions, isOpen, setOpen }) => {
  return <>
    <motion.div
      className="fixed inset-0 z-40 bg-white"
      variants={overlayVariants}
      initial="hidden"
      animate={isOpen ? "visible" : "hidden"}
      exit="hidden"
      onClick={() => setOpen(false)}
    />
    <motion.div
      className="fixed left-4 right-4 bottom-8 bg-base-black rounded-[2rem] z-50 p-[0.375rem] shadow-2xl"
      variants={modalVariants}
      initial="hidden"
      animate={isOpen ? "visible" : "hidden"}
    >
      <div className="flex flex-col gap-1">
        {actions.map((action) => (
          <QuickActionButton
            key={action.text}
            icon={action.icon}
            text={action.text}
            subtext={action.subtext}
            onClick={action.onClick}
            to={action.to}
            className={action.className}
          />
        ))}
      </div>
    </motion.div>
  </>
};

export default QuickActionsModal;
